import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Box, Grid, Typography, Divider, CircularProgress
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { tarefaService, Tarefa, TarefaExecucao } from '../../services/tarefaService';
import { useSnackbar } from 'notistack';
import Swal from 'sweetalert2';

interface AddTarefaModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  tarefa?: Tarefa;
  onExecutar?: (tarefa: Tarefa, referenceDate?: Date) => void;
  instanceDate?: Date;
}

const AddTarefaModal: React.FC<AddTarefaModalProps> = ({ open, onClose, onSuccess, tarefa, onExecutar, instanceDate }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tarefaCompleta, setTarefaCompleta] = useState<Tarefa | null>(null);

  const { control, handleSubmit, watch, reset, formState: { isSubmitting, errors } } = useForm({
    defaultValues: {
      titulo: '',
      descricao: '',
      setor: '',
      hora_tarefa: '',
      tipo_agendamento: 'data',
      data_agendada: '',
      dia_recorrente: 1
    }
  });

  const tipo_agendamento = watch('tipo_agendamento');

  const fetchTarefa = async (id: number) => {
    setLoading(true);
    try {
      const response = await tarefaService.getTarefaById(id);
      if (response.success) {
        const t = response.data;
        setTarefaCompleta(t);
        reset({
          titulo: t.titulo || '',
          descricao: t.descricao || '',
          setor: t.setor || '',
          hora_tarefa: t.hora_tarefa ? t.hora_tarefa.slice(0, 5) : '',
          tipo_agendamento: t.tipo_agendamento || 'data',
          data_agendada: t.data_agendada || '',
          dia_recorrente: t.dia_recorrente || 1
        });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Erro ao carregar dados da tarefa', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      if (tarefa) {
        setIsEditing(false);
        fetchTarefa(tarefa.id);
      } else {
        setIsEditing(true);
        setTarefaCompleta(null);
        reset({
          titulo: '',
          descricao: '',
          setor: '',
          hora_tarefa: '',
          tipo_agendamento: 'data',
          data_agendada: '',
          dia_recorrente: 1
        });
      }
    }
  }, [open, tarefa, reset]);

  const onSubmit = async (data: any) => {
    try {
      const payload = {
        titulo: data.titulo,
        descricao: data.descricao,
        setor: data.setor,
        hora_tarefa: data.hora_tarefa,
        tipo_agendamento: data.tipo_agendamento,
        data_agendada: data.tipo_agendamento === 'data' ? data.data_agendada : undefined,
        dia_recorrente: data.tipo_agendamento === 'recorrente' ? Number(data.dia_recorrente) : undefined
      };

      if (tarefa) {
        await tarefaService.updateTarefa(tarefa.id, payload);
        enqueueSnackbar('Tarefa atualizada com sucesso!', { variant: 'success' });
      } else {
        await tarefaService.createTarefa(payload);
        enqueueSnackbar('Tarefa criada com sucesso!', { variant: 'success' });
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Erro ao salvar tarefa', { variant: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!tarefa) return;

    const result = await Swal.fire({
      title: 'Tem certeza?',
      text: "Você não poderá reverter esta ação!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sim, deletar!',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await tarefaService.deleteTarefa(tarefa.id);
        enqueueSnackbar('Tarefa deletada com sucesso!', { variant: 'success' });
        onSuccess();
        onClose();
      } catch (error) {
        console.error(error);
        enqueueSnackbar('Erro ao deletar tarefa', { variant: 'error' });
      }
    }
  };

  const getMesReferencia = (date: Date) => (
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
  );

  const formatDateOnly = (date?: string) => {
    if (!date) return '';
    const [year, month, day] = date.split('-');
    return day && month && year ? `${day}/${month}/${year}` : date;
  };

  const getExecutorName = (execucao?: TarefaExecucao) => {
    if (!execucao) return '';
    return execucao.nome_executou || execucao.executor?.nome || (execucao.usuario_id_executou ? `Funcionario #${execucao.usuario_id_executou}` : 'Sistema');
  };

  const getExecucaoAtual = (t: Tarefa) => {
    if (t.tipo_agendamento === 'recorrente') {
      const targetDate = instanceDate || new Date();
      const mesReferencia = getMesReferencia(targetDate);

      return (t.execucoes || []).find((exec) => (
        exec.mes_referencia === mesReferencia
      ));
    }

    return (t.execucoes || [])[0];
  };

  const checkIfExecuted = (t: Tarefa) => {
    if (getExecucaoAtual(t)) return true;
    return t.status === 'executada';
  };

  const currentTarefa = tarefaCompleta || tarefa;
  const foiExecutada = currentTarefa ? checkIfExecuted(currentTarefa) : false;
  const execucaoAtual = currentTarefa ? getExecucaoAtual(currentTarefa) : undefined;
  const isReadOnly = tarefa && !isEditing;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {tarefa ? (isEditing ? 'Editar Tarefa' : 'Detalhes da Tarefa') : 'Nova Tarefa'}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers sx={{ minHeight: 200, position: 'relative' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
            </Box>
          ) : (
            <Grid container spacing={1.5}>
              <Grid item xs={8} sm={8} md={8}>
                <Controller
                  name="titulo"
                  control={control}
                  rules={{ required: 'Título é obrigatório' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Título da Tarefa"
                      fullWidth
                      disabled={isReadOnly}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={4} sm={4} md={4}>
                <Controller
                  name="setor"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Setor"
                      fullWidth
                      disabled={isReadOnly}
                      size="small"
                    >
                      <MenuItem value="">Nenhum</MenuItem>
                      <MenuItem value="Financeiro">Financeiro</MenuItem>
                      <MenuItem value="Suporte">Suporte</MenuItem>
                      <MenuItem value="Todos">Todos</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="descricao"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Descrição (Opcional)"
                      fullWidth
                      multiline
                      rows={3}
                      disabled={isReadOnly}
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="tipo_agendamento"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Agendamento"
                      fullWidth
                      disabled={isReadOnly}
                      size="small"
                    >
                      <MenuItem value="data">Data Única</MenuItem>
                      <MenuItem value="recorrente">Recorrente</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                {tipo_agendamento === 'data' ? (
                  <Controller
                    name="data_agendada"
                    control={control}
                    rules={{ required: tipo_agendamento === 'data' ? 'Data é obrigatória' : false }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        type="date"
                        label="Data"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        disabled={isReadOnly}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                ) : (
                  <Controller
                    name="dia_recorrente"
                    control={control}
                    rules={{
                      required: tipo_agendamento === 'recorrente' ? 'Dia é obrigatório' : false,
                      min: { value: 1, message: 'Dia mínimo é 1' },
                      max: { value: 31, message: 'Dia máximo é 31' }
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        type="number"
                        label="Dia do Mês"
                        fullWidth
                        InputProps={{ inputProps: { min: 1, max: 31 } }}
                        disabled={isReadOnly}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                )}
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="hora_tarefa"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="time"
                      label="Hora"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      disabled={isReadOnly}
                      size="small"
                    />
                  )}
                />
              </Grid>

              {currentTarefa && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Criado por: {currentTarefa.criador?.nome || 'Sistema'} em {currentTarefa.createdAt && new Date(currentTarefa.createdAt).toLocaleDateString()}
                  </Typography>
                  {execucaoAtual && (
                    <Typography variant="caption" color="success.main" display="block" fontWeight="bold">
                      Executada por: {getExecutorName(execucaoAtual)} em {formatDateOnly(execucaoAtual.data_execucao)}
                    </Typography>
                  )}
                  {execucaoAtual?.observacoes && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                      Obs: {execucaoAtual.observacoes}
                    </Typography>
                  )}
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Box>
            {tarefa && !isEditing && !loading && (
              <Button color="error" onClick={handleDelete} sx={{ mr: 1 }}>
                Deletar
              </Button>
            )}
          </Box>
          <Box>
            <Button onClick={onClose} sx={{ mr: 1 }}>
              {isReadOnly ? 'Fechar' : 'Cancelar'}
            </Button>

            {tarefa && isReadOnly && !loading && (
              <>
                {!foiExecutada && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setIsEditing(true)}
                    sx={{ mr: 1 }}
                  >
                    Editar
                  </Button>
                )}
                {!foiExecutada && onExecutar && (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => {
                      onClose();
                      onExecutar(currentTarefa || tarefa, instanceDate);
                    }}
                  >
                    Executar
                  </Button>
                )}
              </>
            )}

            {(!tarefa || isEditing) && !loading && (
              <Button type="submit" variant="contained" disabled={isSubmitting}>
                {tarefa ? 'Salvar Alterações' : 'Criar Tarefa'}
              </Button>
            )}
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddTarefaModal;
